import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Crown, CheckCircle2, Lock, CreditCard, X, AlertCircle } from 'lucide-react';

const PLAN_DETAILS = {
  basic: {
    plan_id: 'basic',
    plan_name: 'Basic',
    price_usd: 5,
    amount_cents: 500,
    billing_cycle: 'monthly',
    max_file_size: '100GB',
    storage_quota: '100GB',
    retention: 'While subscribed',
    description: 'Keep up to 100 GB of files and links active while subscribed'
  },
  pro: {
    plan_id: 'pro',
    plan_name: 'Pro',
    price_usd: 15,
    amount_cents: 1500,
    billing_cycle: 'monthly',
    max_file_size: '500GB',
    storage_quota: '500GB',
    retention: 'While subscribed',
    description: 'Keep up to 500 GB of files and links active while subscribed'
  },
  premium: {
    plan_id: 'premium',
    plan_name: 'Premium',
    price_usd: 30,
    amount_cents: 3000,
    billing_cycle: 'monthly',
    max_file_size: '1TB',
    storage_quota: '1TB',
    retention: 'While subscribed',
    description: 'Keep up to 1 TB of files and links active while subscribed'
  },
  basic_yearly: {
    plan_id: 'basic_yearly',
    plan_name: 'Basic (Yearly)',
    price_usd: 50,
    amount_cents: 5000,
    billing_cycle: 'yearly',
    max_file_size: '100GB',
    storage_quota: '100GB',
    retention: 'While subscribed',
    description: 'Keep up to 100 GB of files and links active for a full year'
  },
  pro_yearly: {
    plan_id: 'pro_yearly',
    plan_name: 'Pro (Yearly)',
    price_usd: 150,
    amount_cents: 15000,
    billing_cycle: 'yearly',
    max_file_size: '500GB',
    storage_quota: '500GB',
    retention: 'While subscribed',
    description: 'Keep up to 500 GB of files and links active for a full year'
  },
  premium_yearly: {
    plan_id: 'premium_yearly',
    plan_name: 'Premium (Yearly)',
    price_usd: 300,
    amount_cents: 30000,
    billing_cycle: 'yearly',
    max_file_size: '1TB',
    storage_quota: '1TB',
    retention: 'While subscribed',
    description: 'Keep up to 1 TB of files and links active for a full year'
  }
};

const CheckoutModal = ({ isOpen, onClose, planId = 'basic', userEmail, userId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [plan, setPlan] = useState(PLAN_DETAILS[planId] || PLAN_DETAILS.basic);
  const { user } = useAuth();

  const fetchPlanDetails = useCallback(() => {
    const selected = PLAN_DETAILS[planId] || PLAN_DETAILS.basic;
    setPlan(selected);
  }, [planId]);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setPaymentSuccess(false);
      fetchPlanDetails();
    }
  }, [isOpen, planId, fetchPlanDetails]);

  const handlePayment = async () => {
    const email = userEmail || user?.email;
    const uid = userId || (user?.uid ? `user_${user.uid}` : localStorage.getItem('fileshare_user_id'));
    
    if (!email) {
      setError('Please sign in or provide your email to complete checkout.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiBase = '';
      const returnUrl = `${window.location.origin}/pricing?status=success&plan=${plan?.plan_id || 'basic'}`;

      // Call backend to create Dodo Payments subscription checkout session
      const res = await fetch(`${apiBase}/api/subscription/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: plan?.plan_id || 'basic',
          userEmail: email,
          userId: uid || email,
          returnUrl: returnUrl
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success || !data.paymentUrl) {
        throw new Error(data.error || 'Failed to initialize checkout session');
      }

      // Redirect directly to Dodo Payments hosted checkout
      window.location.href = data.paymentUrl;

    } catch (err) {
      console.error('[Checkout] error:', err);
      setError(err.message || 'Payment checkout could not be started.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      style={{ backdropFilter: 'blur(6px)', fontFamily: "'Largetransfer', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
    >
      <div 
        style={{ 
          backgroundColor: '#12141a', 
          border: '1px solid #1f232e', 
          borderRadius: '8px', 
          padding: '28px', 
          width: '100%', 
          maxWidth: '440px', 
          position: 'relative',
          boxShadow: '0 20px 40px -15px rgba(0,0,0,0.7)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
        >
          <X size={18} />
        </button>

        {/* Modal Header */}
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '8px',
            backgroundColor: '#181a22',
            border: '1px solid #242834',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px auto'
          }}>
            <Crown size={20} color="#ffffff" />
          </div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', margin: '0 0 6px 0', letterSpacing: '-0.02em' }}>
            Complete Your Upgrade
          </h2>
          <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
            Keep your files and share links active continuously while subscribed.
          </p>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Plan Summary Card */}
          <div style={{ 
            backgroundColor: '#181a22', 
            borderRadius: '6px', 
            padding: '16px',
            border: '1px solid #242834' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Crown size={16} color="#ffffff" />
                <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '14px' }}>{plan?.plan_name || 'Basic'} Plan</span>
              </div>
              <div style={{ 
                backgroundColor: '#20242f', 
                color: '#ffffff', 
                border: '1px solid #282d3b',
                padding: '3px 10px', 
                borderRadius: '9999px', 
                fontSize: '12px', 
                fontWeight: '600' 
              }}>
                ${plan?.price_usd || 5}/{plan?.billing_cycle === 'yearly' ? 'year' : 'month'}
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle2 size={15} color="#10b981" />
                <span style={{ color: '#d1d5db' }}>Up to {plan?.max_file_size || '100GB'} individual file size</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle2 size={15} color="#10b981" />
                <span style={{ color: '#d1d5db' }}>{plan?.storage_quota || '100GB'} storage quota</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle2 size={15} color="#10b981" />
                <span style={{ color: '#ffffff', fontWeight: '500' }}>Files stay active as long as subscribed</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle2 size={15} color="#10b981" />
                <span style={{ color: '#d1d5db' }}>Continuous retention & unthrottled transfers</span>
              </div>
            </div>
          </div>

          {/* Checkout Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#9ca3af', backgroundColor: '#181a22', padding: '10px 12px', borderRadius: '6px', border: '1px solid #242834' }}>
            <Lock size={15} color="#9ca3af" style={{ flexShrink: 0 }} />
            <span>Secure checkout. Card, Apple Pay, Google Pay.</span>
          </div>

          {/* Success Display */}
          {paymentSuccess && (
            <div style={{ 
              backgroundColor: 'rgba(16, 185, 129, 0.1)', 
              border: '1px solid rgba(16, 185, 129, 0.3)',
              color: '#34d399', 
              padding: '12px 14px', 
              borderRadius: '6px', 
              fontSize: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px' 
            }}>
              <CheckCircle2 size={16} />
              <span>Payment verified! Subscription is active.</span>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div style={{ 
              backgroundColor: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#f87171', 
              padding: '12px 14px', 
              borderRadius: '6px', 
              fontSize: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px' 
            }}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Payment CTA Button */}
          <button 
            id="dodo-checkout-button"
            onClick={handlePayment}
            disabled={loading || paymentSuccess}
            style={{ 
              backgroundColor: '#ffffff', 
              color: '#0a0b0d', 
              padding: '11px 18px', 
              borderRadius: '6px', 
              border: 'none', 
              cursor: (loading || paymentSuccess) ? 'not-allowed' : 'pointer',
              fontSize: '13px', 
              fontWeight: '600',
              width: '100%',
              opacity: (loading || paymentSuccess) ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background-color 0.2s ease, opacity 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading && !paymentSuccess) {
                e.currentTarget.style.backgroundColor = '#f4f4f5';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !paymentSuccess) {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            {loading ? (
              <span>Redirecting to Checkout...</span>
            ) : paymentSuccess ? (
              <span>Payment Verified ✓</span>
            ) : (
              <>
                <CreditCard size={16} />
                <span>Continue to Checkout (${plan?.price_usd || 5}/{plan?.billing_cycle === 'yearly' ? 'yr' : 'mo'})</span>
              </>
            )}
          </button>

          {/* Cancel */}
          <div style={{ textAlign: 'center' }}>
            <button 
              onClick={onClose}
              style={{ 
                backgroundColor: 'transparent', 
                color: '#9ca3af', 
                padding: '6px 12px', 
                borderRadius: '4px', 
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px', 
                fontWeight: '500',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;